import Button from "../Button";
import { ArrowCircleLeft } from "@mui/icons-material";
import { Dialog, Transition } from "@headlessui/react";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment, useEffect, useState } from "react";
import { PopupContext } from "@contexts";
import { useSession } from "@hooks";
import clsx from "clsx";

type PopupProps = {
  btnElement: (onClick: () => void) => JSX.Element;
  btnCloseText?: string;
  open?: boolean;
  title?: string;
  onClose?: () => void;
  enableClose?: boolean;
  showBtnClose?: boolean;
  size?: "md" | "lg";
  newCancelButton?: boolean;
};

const languagesVarsCodes = [
  "LANG_neobooking_popup_close", //Fermer
];

const Popup: React.FC<PopupProps> = ({
  btnElement,
  btnCloseText,
  open = false,
  title,
  children,
  onClose,
  enableClose = true,
  showBtnClose = true,
  size = "md",
  newCancelButton = false,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  /* State */
  const [isOpen, setIsOpen] = useState<boolean>(open);
  const [_btnCloseText, setBtnCloseText] = useState<string>(
    btnCloseText ?? "Fermer"
  );

  useEffect(() => {
    if (!btnCloseText) {
      setBtnCloseText(getLanguageVarValue("LANG_neobooking_popup_close"));
    }
  }, []);

  function closeModal() {
    if (enableClose) {
      setIsOpen(false);
      onClose && onClose();
    }
  }
  function openModal() {
    setIsOpen(true);
  }

  return (
    <>
      <PopupContext.Provider value={{ isOpen, setIsOpen }}>
        {btnElement(openModal)}
        <Transition appear show={isOpen} as={Fragment}>
          <Dialog
            as="div"
            className="tw-fixed tw-inset-0 tw-overflow-y-auto"
            onClose={closeModal}
            style={{
              backgroundColor: "rgba(209, 213, 219, 0.7)",
              zIndex: 1098,
            }}
          >
            <div className="tw-min-h-screen tw-px-4 tw-text-center">
              <Transition.Child
                as={Fragment}
                enter="tw-ease-out tw-duration-300"
                enterFrom="tw-opacity-0"
                enterTo="tw-opacity-100"
                leave="tw-ease-in tw-duration-200"
                leaveFrom="tw-opacity-100"
                leaveTo="tw-opacity-0"
              >
                <Dialog.Overlay className="tw-fixed tw-inset-0" />
              </Transition.Child>
              {/* This element is to trick the browser into centering the modal contents. */}
              <span
                className="tw-inline-block tw-h-screen tw-align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="tw-ease-out tw-duration-300"
                enterFrom="tw-opacity-0 tw-scale-95"
                enterTo="tw-opacity-100 tw-scale-100"
                leave="tw-ease-in tw-duration-200"
                leaveFrom="tw-opacity-100 tw-scale-100"
                leaveTo="tw-opacity-0 tw-scale-95"
              >
                <div
                  className={`tw-inline-block tw-w-full ${
                    size === "md" ? "tw-max-w-3xl" : "tw-max-w-5xl"
                  } tw-p-6 tw-my-8 tw-overflow-hidden tw-text-left tw-align-middle tw-transition-all tw-transform tw-bg-white tw-shadow-xl tw-rounded`}
                >
                  <div
                    className={`tw-flex tw-titre-principal-spirit tw-gap-4 ${
                      title ? "tw-justify-between" : "tw-justify-end"
                    } tw-items-start`}
                  >
                    {title && (
                      <Dialog.Title
                        as="h3"
                        className="tw-text-2xl tw-font-medium"
                      >
                        {title}
                      </Dialog.Title>
                    )}
                    {enableClose && (
                      <Button
                        type="button"
                        onClick={closeModal}
                        className="hover:tw-text-v_blue-dark/80 tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out"
                      >
                        <FontAwesomeIcon
                          className="tw-text-sm"
                          icon={faTimes}
                        />
                      </Button>
                    )}
                  </div>
                  <div className={enableClose || title ? "tw-mt-8" : ""}>
                    {children}
                  </div>
                  {enableClose && showBtnClose && (
                    <div
                      className={clsx(
                        "tw-mt-4 tw-flex",
                        newCancelButton && newCancelButton
                          ? "tw-justify-center"
                          : "tw-justify-end"
                      )}
                    >
                      {newCancelButton && newCancelButton ? (
                        <Button
                          shape="pills"
                          variant="danger"
                          additionalClassname="tw-w-auto tw-flex tw-justify-center tw-items-center tw-gap-x-2 tw-font-bold"
                          onClick={closeModal}
                        >
                          <ArrowCircleLeft />
                          {_btnCloseText}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="primary"
                          shape="pills"
                          additionalClassname="tw-w-full md:tw-w-auto"
                          onClick={closeModal}
                        >
                          {_btnCloseText}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      </PopupContext.Provider>
    </>
  );
};

Popup.propTypes = {
  //
};

export default Popup;
